import { useEffect, useState } from 'react'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import { organizationService } from '../../services'
import { formatOrganizationLabel } from '../../utils/organizationLabel'

const DEBOUNCE_MS = 300

/**
 * Searchable Shop picker (name or shop id). Partial search via admin orgs API.
 * Value shape: { id: string, label: string } | null
 */
export default function ShopAutocomplete({
  value,
  onChange,
  label = 'Shop',
  required = false,
  disabled = false,
  size = 'small',
  fullWidth = true,
  helperText,
}) {
  const [inputValue, setInputValue] = useState(value?.label || '')
  const [searchText, setSearchText] = useState('')
  const [options, setOptions] = useState(value ? [value] : [])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (value?.label && value.label !== inputValue && !searchText) {
      setInputValue(value.label)
    }
    // Keep selected shop visible in options after async refresh.
    if (value && !options.some((o) => o.id === value.id)) {
      setOptions((prev) => [value, ...prev.filter((o) => o.id !== value.id)])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id, value?.label])

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        setLoading(true)
        const { items: rows } = await organizationService.getOrganizationsPage({
          page: 1,
          limit: 25,
          search: searchText.trim() || undefined,
        })
        if (cancelled) return
        const mapped = (Array.isArray(rows) ? rows : []).map((org) => ({
          id: String(org.id),
          label: formatOrganizationLabel(org),
          name: org.organization_name || org.name || '',
        }))
        if (value && !mapped.some((o) => o.id === value.id)) {
          setOptions([value, ...mapped])
        } else {
          setOptions(mapped)
        }
      } catch {
        if (!cancelled) setOptions(value ? [value] : [])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [searchText, value])

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      loading={loading}
      options={options}
      filterOptions={(opts) => opts}
      value={value}
      inputValue={inputValue}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onChange={(_, option) => {
        onChange?.(option)
        setInputValue(option?.label || '')
        if (!option) setSearchText('')
      }}
      onInputChange={(_, next, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(next || '')
          setSearchText(next || '')
          if (reason === 'clear') onChange?.(null)
        } else if (reason === 'reset') {
          setInputValue(next || '')
        }
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText || 'Search by shop name or shop id'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}
